import React from "react";
import type { WBSTreeNode, WBSActivity } from "../../../models";
import ActivityTable from "../ActivityTable/ActivityTable";
import "./TreeNode.css";

interface Props {
  node: WBSTreeNode;
  depth: number;
  isExpanded: boolean;
  isLoading: boolean;
  children: WBSTreeNode[];
  activities: WBSActivity[];
  onToggle: (node: WBSTreeNode) => void;
  getChildren: (nodeId: number) => WBSTreeNode[];
  getActivities: (nodeId: number) => WBSActivity[];
  isNodeExpanded: (nodeId: number) => boolean;
  isNodeLoading: (nodeId: number) => boolean;
}

const TreeNode: React.FC<Props> = ({
  node,
  depth,
  isExpanded,
  isLoading,
  children,
  activities,
  onToggle,
  getChildren,
  getActivities,
  isNodeExpanded,
  isNodeLoading,
}) => {
  const indent = depth * 20;
  const hasExpandable = node.has_children;
  const { complete, active, not_started } = node.status_summary;

  return (
    <>
      {/* WBS node row */}
      <div
        className={`tree-node ${!hasExpandable ? "tree-node-empty" : ""}`}
        style={{ paddingLeft: `${indent + 12}px` }}
        onClick={() => hasExpandable && onToggle(node)}
      >
        <span
          className={`tree-chevron ${hasExpandable ? "" : "tree-chevron-hidden"} ${isExpanded ? "tree-chevron-expanded" : ""}`}
        >
          {isLoading ? <span className="tree-spinner" /> : "\u25B6"}
        </span>

        <span className="tree-node-name" title={node.name}>
          {node.name}
        </span>

        {node.activity_count > 0 && (
          <span className="tree-node-count">{node.activity_count}</span>
        )}

        <span className="tree-status-bar">
          {complete > 0 && (
            <span className="tree-status tree-status-complete">{complete}</span>
          )}
          {active > 0 && (
            <span className="tree-status tree-status-active">{active}</span>
          )}
          {not_started > 0 && (
            <span className="tree-status tree-status-notstart">
              {not_started}
            </span>
          )}
        </span>
      </div>

      {/* Activities attached directly to this node — shown first, so a branch
          node's own activities appear right under it, before its sub-nodes. */}
      {isExpanded && activities.length > 0 && (
        <div style={{ paddingLeft: `${indent + 32 + 12}px` }}>
          <ActivityTable activities={activities} />
        </div>
      )}

      {/* Child WBS nodes */}
      {isExpanded &&
        children.map((child) => (
          <TreeNode
            key={child.id}
            node={child}
            depth={depth + 1}
            isExpanded={isNodeExpanded(child.id)}
            isLoading={isNodeLoading(child.id)}
            children={getChildren(child.id)}
            activities={getActivities(child.id)}
            onToggle={onToggle}
            getChildren={getChildren}
            getActivities={getActivities}
            isNodeExpanded={isNodeExpanded}
            isNodeLoading={isNodeLoading}
          />
        ))}
    </>
  );
};

export default TreeNode;
